// frontend/src/Components/Tasks/MentionTextarea.jsx
//
// Controlled textarea with `@` autocomplete. Tracks an internal `mentions`
// map (display name → user id) so on submit we hand the parent a clean
// `{ text, mentionedUserIds }` payload — the parent doesn't need to re-parse
// the textarea content.
//
// Detection: when the user types `@`, we open a dropdown of practice users
// (filtered by what they type after the `@`). Selecting a user inserts
// `@FirstName Lastname ` into the text and records the mention.
//
// Validation lives server-side (tasksRoutes.js) — this component is UX only.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, ListGroup } from 'react-bootstrap';

function findActiveMention(text, caret) {
  // Walk backwards from caret looking for `@` that's at start-of-string OR
  // preceded by whitespace. Stops at whitespace (no mention in progress).
  if (caret <= 0) return null;
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      const before = i === 0 ? ' ' : text[i - 1];
      if (/\s/.test(before)) {
        return { atIndex: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i--;
  }
  return null;
}

export default function MentionTextarea({
  value,
  onChange,
  practiceUsers = [],
  placeholder = 'Add a comment…',
  rows = 2,
  disabled = false,
  // External signal: when the parent calls `clear()` via this ref-like prop,
  // we drop our internal mentions map. Done via a key change instead — keep
  // it simple. Caller passes a unique `resetSignal` to wipe state.
  resetSignal = 0,
  // Optional style override — useful when the parent wants a guaranteed
  // min-height (e.g. 8 rows that scroll on overflow rather than expanding).
  textareaStyle,
}) {
  const textareaRef = useRef(null);
  const [active, setActive] = useState(null); // { atIndex, query } | null
  const [mentions, setMentions] = useState({}); // { [displayName]: userId }

  // Reset mentions when caller bumps resetSignal (after submit success).
  useEffect(() => { setMentions({}); }, [resetSignal]);

  const filteredUsers = useMemo(() => {
    if (!active) return [];
    const q = (active.query || '').toLowerCase();
    return practiceUsers
      .filter((u) => {
        const name = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        return !q || name.includes(q);
      })
      .slice(0, 6);
  }, [active, practiceUsers]);

  // Recompute mentionedUserIds from current text. A name is "still mentioned"
  // if its display label appears prefixed with `@` in the text.
  const recomputeMentionedIds = (text) => {
    const ids = [];
    for (const [name, uid] of Object.entries(mentions)) {
      if (text.includes(`@${name}`)) ids.push(uid);
    }
    return Array.from(new Set(ids));
  };

  const handleChange = (e) => {
    const next = e.target.value;
    const caret = e.target.selectionStart || next.length;
    const a = findActiveMention(next, caret);
    setActive(a);
    onChange?.({ text: next, mentionedUserIds: recomputeMentionedIds(next) });
  };

  const insertMention = (user) => {
    const ta = textareaRef.current;
    if (!ta || !active) return;
    const before = ta.value.slice(0, active.atIndex);
    const after = ta.value.slice(ta.selectionStart || ta.value.length);
    const display = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'user';
    const inserted = `@${display} `;
    const next = before + inserted + after;
    const newMentions = { ...mentions, [display]: String(user._id) };
    setMentions(newMentions);
    setActive(null);
    onChange?.({
      text: next,
      mentionedUserIds: Array.from(new Set(
        Object.entries(newMentions)
          .filter(([name]) => next.includes(`@${name}`))
          .map(([, uid]) => uid),
      )),
    });
    // Restore caret position right after the inserted mention.
    setTimeout(() => {
      ta.focus();
      const pos = before.length + inserted.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <div className="position-relative">
      <Form.Control
        as="textarea"
        ref={textareaRef}
        rows={rows}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        style={textareaStyle}
      />
      {active && filteredUsers.length > 0 && (
        <ListGroup
          className="position-absolute shadow"
          style={{ zIndex: 1080, top: '100%', left: 0, right: 0, maxHeight: 220, overflowY: 'auto' }}
        >
          {filteredUsers.map((u) => (
            <ListGroup.Item
              key={u._id}
              action
              onClick={() => insertMention(u)}
              className="py-1 small"
            >
              <i className="fa-light fa-at me-2 text-muted" />
              {`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}
              {u.email && <span className="text-muted ms-2">{u.email}</span>}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
}
